import React, { useCallback, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  IconSizes,
  LockShieldLineIcon,
  SquareCheckmarkLineIcon
} from '@inno-ui/icons'
import { Modal } from '@inno-ui/modal'
import { TextInput } from '@inno-ui/text-input'
import { toast } from '@inno-ui/toast'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { useDisableTwoFactor } from 'shared/api/auth/requests/2fa/disableTwoFactor'
import { useSendDeactivationCode } from 'shared/api/auth/requests/2fa/sendDeactivationCode'

import { OtpInput } from '../OtpInput'
import style from './DisableTwoFactorModal.module.scss'
import { DisableTwoFactorModalProps } from './DisableTwoFactorModal.types'

export const DisableTwoFactorModal: React.FC<DisableTwoFactorModalProps> = ({
  isOpen,
  setIsOpen,
  onSuccess: onSuccessToggle,
  onCancel
}) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'disable_two_factor_modal'
  })
  const [otp, setOtp] = useState('')
  const [code, setCode] = useState({ show: false, input: '' })
  const [otpDisabled, setOtpDisabled] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleCLoseModal = () => {
    setIsError(false)
    setOtpDisabled(false)
    setOtp('')
    setCode({ show: false, input: '' })
    onCancel()
  }

  const onSuccess = () => {
    toast.success({
      title: t('toast_success_title'),
      subTitle: t('toast_success_message')
    })
    handleCLoseModal()
    onSuccessToggle()
  }

  const onError = () => {
    setOtp('')
    setCode(prev => ({ ...prev, input: '' }))
    setIsError(true)
  }

  const {
    mutation: { mutate: disableTwoFactorMutate, isPending: isLoading }
  } = useDisableTwoFactor({ onSuccess, onError })

  const onSendDeactivationCodeSuccess = () => {
    setCode({ show: true, input: '' })
  }

  const {
    mutation: {
      mutate: sendDeactivationCodeMutate,
      isPending: isLoadingDeactivateMailRequest
    }
  } = useSendDeactivationCode({ onSuccess: onSendDeactivationCodeSuccess })

  const onConfirm = useCallback(() => {
    if (code.show) {
      disableTwoFactorMutate({ code: code.input })
    } else {
      disableTwoFactorMutate({ otp: otp.toString() })
    }
  }, [otp, code, disableTwoFactorMutate])

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCLoseModal}
      setIsOpen={setIsOpen}
      zIndex={1003}
      maxWidth={800}
      maxHeight={630}
      layers={[
        {
          icon: LockShieldLineIcon,
          title: t('header'),
          bodyPaddings: false,
          body: (
            <div className={style.wrapper}>
              <div className={style.content}>
                <div className={style.activateItem}>
                  <div
                    className={cn(
                      style.title,
                      otpDisabled ? style.success : undefined
                    )}
                  >
                    <SquareCheckmarkLineIcon size={IconSizes.Size34} />
                    <span>{t('title')}</span>
                  </div>
                  {code.show ? (
                    <>
                      <TextInput
                        value={code.input}
                        onChange={e =>
                          setCode(prev => ({ ...prev, input: e.target.value }))
                        }
                        label={t('code_label')}
                        autoFocus
                      />
                    </>
                  ) : (
                    <>
                      <span>{t('otp_label')}</span>
                      <OtpInput
                        value={otp}
                        onChange={setOtp}
                        isLoading={isLoading || isLoadingDeactivateMailRequest}
                        onComplete={onConfirm}
                      />
                    </>
                  )}
                  <div className={style.buttonsContainer}>
                    {isError ? (
                      <span className={style.errorMessage}>
                        {t('error_message')}
                      </span>
                    ) : null}
                    {otpDisabled ? (
                      <span className={style.successMessage}>
                        {t('success_message')}
                      </span>
                    ) : (
                      <Button
                        variant={ButtonVariants.Dark}
                        disabled={isLoading || isLoadingDeactivateMailRequest}
                        onClick={onConfirm}
                        label={'Confirm'}
                        type={'submit'}
                      />
                    )}
                  </div>
                  {!code.show && !otpDisabled ? (
                    <span
                      onClick={() => {
                        sendDeactivationCodeMutate()
                      }}
                      className={cn(
                        style.sendMail,
                        isLoadingDeactivateMailRequest
                          ? style.disabled
                          : undefined
                      )}
                    >
                      {t('send_mail_text')}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          )
        }
      ]}
    />
  )
}
